<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import conversationFlowExecutionsAPI from 'dashboard/api/conversationFlowExecutions';
import Spinner from 'shared/components/Spinner.vue';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import parseISO from 'date-fns/parseISO';
import { useDateFnsLocale } from 'dashboard/composables/useDateFnsLocale';
import FlowExecutionLogModal from './FlowExecutionLogModal.vue';

const props = defineProps({
  conversationId: {
    type: [String, Number],
    required: true,
  },
});

const { t } = useI18n();
const { dateFnsLocale } = useDateFnsLocale();

const loading = ref(false);
const executions = ref([]);
const selectedExecution = ref(null);
const showLogModal = ref(false);

const STATUS_CONFIG = {
  completed: {
    label: 'FLOW_EXECUTIONS.STATUS.COMPLETED',
    class:
      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  },
  failed: {
    label: 'FLOW_EXECUTIONS.STATUS.FAILED',
    class: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  },
  running: {
    label: 'FLOW_EXECUTIONS.STATUS.RUNNING',
    class: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  },
  timeout: {
    label: 'FLOW_EXECUTIONS.STATUS.TIMEOUT',
    class:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  },
  cancelled: {
    label: 'FLOW_EXECUTIONS.STATUS.CANCELLED',
    class: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
  },
};

const fetchExecutions = async () => {
  loading.value = true;
  try {
    const response = await conversationFlowExecutionsAPI.get(
      props.conversationId
    );
    executions.value = response.data.data || [];
  } catch (error) {
    // error loading flow executions
  } finally {
    loading.value = false;
  }
};

const openLogModal = async execution => {
  try {
    const response = await conversationFlowExecutionsAPI.show(
      props.conversationId,
      execution.id
    );
    selectedExecution.value = response.data;
    showLogModal.value = true;
  } catch (error) {
    // error loading execution details
  }
};

const closeLogModal = () => {
  showLogModal.value = false;
  selectedExecution.value = null;
};

const getStatusConfig = status => {
  return STATUS_CONFIG[status] || STATUS_CONFIG.cancelled;
};

const formatRelativeTime = dateString => {
  if (!dateString) return '';
  return formatDistanceToNow(parseISO(dateString), {
    addSuffix: true,
    locale: dateFnsLocale.value,
  });
};

watch(() => props.conversationId, fetchExecutions);
onMounted(fetchExecutions);
</script>

<template>
  <div class="conversation-flow-executions">
    <div v-if="loading" class="flex justify-center p-4">
      <Spinner />
    </div>

    <div v-else-if="executions.length > 0" class="p-2 space-y-2">
      <div
        v-for="execution in executions"
        :key="execution.id"
        class="border border-slate-200 dark:border-slate-700 rounded-lg p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
        :class="{
          'border-l-4 border-l-red-400': execution.status === 'failed',
          'border-l-4 border-l-yellow-400': execution.status === 'timeout',
        }"
        @click="openLogModal(execution)"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <fluent-icon
                icon="arrow-swap"
                size="14"
                class="text-slate-500 dark:text-slate-400 shrink-0"
              />
              <span
                class="text-sm font-medium text-slate-700 dark:text-slate-200 truncate"
                :title="execution.flow?.name"
              >
                {{ execution.flow?.name || t('FLOW_EXECUTIONS.UNKNOWN_FLOW') }}
              </span>
            </div>
            <div class="flex items-center gap-2 flex-wrap">
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                :class="getStatusConfig(execution.status).class"
              >
                {{ t(getStatusConfig(execution.status).label) }}
              </span>
              <span class="text-xs text-slate-400 dark:text-slate-500">
                {{ formatRelativeTime(execution.started_at) }}
              </span>
            </div>
          </div>
          <div
            class="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 shrink-0"
          >
            <span
              v-if="execution.nodes_executed > 0"
              :title="t('FLOW_EXECUTIONS.NODES_EXECUTED')"
            >
              {{ execution.nodes_executed }}
              <fluent-icon
                icon="checkmark-circle"
                size="12"
                class="inline text-green-500"
              />
            </span>
            <span
              v-if="execution.nodes_failed > 0"
              :title="t('FLOW_EXECUTIONS.NODES_FAILED')"
            >
              {{ execution.nodes_failed }}
              <fluent-icon
                icon="error-circle"
                size="12"
                class="inline text-red-500"
              />
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-6 px-4">
      <fluent-icon
        icon="arrow-swap"
        size="28"
        class="text-slate-300 dark:text-slate-600 mb-2"
      />
      <p class="text-sm text-slate-500 dark:text-slate-400">
        {{ t('FLOW_EXECUTIONS.EMPTY_STATE') }}
      </p>
    </div>

    <FlowExecutionLogModal
      v-if="showLogModal && selectedExecution"
      :execution="selectedExecution"
      @close="closeLogModal"
    />
  </div>
</template>
